import { useCallback, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { v4 as uuidv4 } from 'uuid';

import {
  Criterion,
  Feature,
  Field,
  Form,
  isSelectChoiceField,
  ListValueType,
  SelectionBehavior,
  StudyType,
  usePlan,
  useSession,
  useShortOrganizationSessions,
} from '@app/api';
import { isUUID, OptionsObject, Select } from '@app/form';
import {
  Button,
  CenteredModal,
  IconChip,
  IconMutedButton,
  InformationBox,
  LabeledSection,
} from '@app/core';
import { useFormSession } from '../FormBuilder/lib/useFormSession';
import { getMaxNumberOfQuestions } from 'lib/billing';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useFeatureRequiredCallback } from 'components';
import { validationFieldTypes } from 'components/CustomForm/fieldConfigurations';

const useStyles = createUseStyles((theme: Theme) => ({
  duplicateContainer: {
    minHeight: 140,
    margin: [theme.marginBase * 3, 0],
    display: 'flex',
    flexDirection: 'column',
  },
  buttonModal: {
    width: '100%',
  },
  select: {
    marginTop: 0,
  },
  icon: {
    marginLeft: theme.marginBase,
  },
  mainContainer: {
    margin: [theme.marginBase * 3, 0],
  },
  buttonDuplicate: {
    marginTop: theme.marginBase * 2,
    display: 'flex',
    justifyContent: 'revert',
  },
  iconContainer: {
    display: 'inline-flex',
    width: 24,
    padding: 0,
  },
  iconText: {
    fontSize: 14,
    padding: 0,
  },
}));

interface Props {
  form?: Partial<Form>;
  onChange: (form?: Partial<Form>) => any;
  criteria?: Criterion[];
  onCriteriaChange?: (criteria: Criterion[]) => any;
  sessionId?: string;
}
export const DuplicateFormSession = ({
  form,
  onChange,
  criteria,
  onCriteriaChange,
  sessionId,
}: Props) => {
  const classes = useStyles();
  const [openModal, setOpenModal] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const fields = (form && form.fields) || [];

  const [
    isUnmoderatedDuplicationNotConform,
    setIsUnmoderatedDuplicationNotConform,
  ] = useState(false);

  const { data: session } = useSession(sessionId);

  const { data: allOrgSessions } = useShortOrganizationSessions(
    session?.organizationId,
  );
  const [sessionToDuplicateId, setSessionToDuplicateId] = useState<string>();

  const allSessionId = useMemo(
    () =>
      allOrgSessions?.reduce((acc, session) => {
        const label = session.internalName || session.title;
        if (label && session.id !== sessionId) {
          acc.push({ label, value: session.id });
        }
        return acc;
      }, [] as OptionsObject[]),
    [allOrgSessions, sessionId],
  );

  const { SessionForm: formToDuplicate, SessionCriteria: criteriaToDuplicate } =
    useFormSession(sessionToDuplicateId || '');

  const { data: plan } = usePlan(session && session.planId);
  const maxNumberOfQuestions = getMaxNumberOfQuestions(plan?.products);

  const getNumberOfQuestions = (form?: Partial<Form>) => {
    if (!form || !form?.fields || form.fields.length === 0) {
      return 0;
    }
    return form?.fields?.length || 0;
  };

  const numberOfQuestionsToDuplicate = useMemo(() => {
    return getNumberOfQuestions(formToDuplicate);
  }, [formToDuplicate]);

  const numberOfQuestions = useMemo(() => {
    return getNumberOfQuestions(form);
  }, [form]);

  const duplicateCriteriaField = useCallback(
    (fieldRefMap: Record<string, string>, newFields: Field[]) => {
      if (!criteriaToDuplicate || !onCriteriaChange) {
        return;
      }
      const isUnmoderated = session?.studyType === StudyType.UNMODERATED;

      const newCriteria: Criterion[] = [...(criteria || [])];
      for (const criterion of criteriaToDuplicate) {
        if (!fieldRefMap[criterion.fieldRef]) {
          continue;
        }
        if (!criterion.screenerCriterion) {
          continue;
        }
        const result = {
          ...criterion,
          id: uuidv4(),
          fieldRef: fieldRefMap[criterion.fieldRef],
        };

        const field = newFields?.find((field) => field.ref === result.fieldRef);
        if (!field) {
          continue;
        }

        if (!isUnmoderated) {
          newCriteria.push(result);
          continue;
        }

        if (!isSelectChoiceField(field)) {
          newCriteria.push(result);
          continue;
        }

        const validationChoicesIsNotSet = !!field.properties.choices?.some(
          (choice) =>
            criterion.allowList?.includes(choice.ref) &&
            !criterion.automaticlyAcceptedList?.includes(choice.ref),
        );
        if (validationChoicesIsNotSet) {
          setIsUnmoderatedDuplicationNotConform(true);
        }
        result.allowList = result.allowList || [];
        result.automaticlyAcceptedList = result.automaticlyAcceptedList || [];
        result.denyList = result.denyList || [];

        const emptyOptions = result.allowList.filter(
          (ref) => !result.automaticlyAcceptedList!.includes(ref),
        );
        result.allowList = result.automaticlyAcceptedList;
        result.denyList.push(...emptyOptions);
        newCriteria.push(result);
      }

      if (isUnmoderated) {
        for (const field of newFields) {
          if (newCriteria?.some((c) => c.fieldRef === field.ref)) {
            continue;
          }
          if (!isSelectChoiceField(field)) {
            continue;
          }
          newCriteria.push({
            allowList: null,
            screenerCriterion: true,
            automaticlyAcceptedList: [],
            listValueType: ListValueType.KEYWORD,
            denyList:
              field.properties.choices?.map((choice) => choice.ref) || [],
            fieldRef: field.ref,
            id: uuidv4(),
            selectionBehavior: SelectionBehavior.OPTIONAL,
            tolerateList: null,
          });
          setIsUnmoderatedDuplicationNotConform(true);
        }
      }
      onCriteriaChange(newCriteria);
    },
    [criteria, criteriaToDuplicate, fields, onCriteriaChange, session],
  );

  const onDuplicateSessionFormSubmit = useCallback(() => {
    if (!formToDuplicate) {
      return;
    }
    setIsLoading(true);
    const formId = form?.id || uuidv4();
    const fieldsToCopy = formToDuplicate?.fields || [];

    let fieldsCopy: Field[] = [];
    const fieldIdMap: Record<string, string> = {};
    const fieldRefMap: Record<string, string> = {};

    // duplicate Fields
    for (const field of fieldsToCopy) {
      if (
        session?.studyType === StudyType.UNMODERATED &&
        !validationFieldTypes.includes(field.type)
      ) {
        setIsUnmoderatedDuplicationNotConform(true);
        continue;
      }
      const newId = uuidv4();
      const newRef = isUUID(field.ref) ? uuidv4() : field.ref;
      fieldsCopy.push({
        ...field,
        id: newId,
        ref: newRef,
      });
      fieldIdMap[field.id] = newId;
      fieldRefMap[field.ref] = newRef;
    }
    fieldsCopy = fieldsCopy.map((field) => ({
      ...field,
      jumpActions: field.jumpActions?.map((jumpAction) => ({
        ...jumpAction,
        id: uuidv4(),
        toFieldId: jumpAction.toFieldId
          ? fieldIdMap[jumpAction.toFieldId]
          : undefined,
      })),
    }));
    // duplicate criteria field
    duplicateCriteriaField(fieldRefMap, fieldsCopy);
    onChange({
      ...form,
      fields: fieldsCopy,
      id: formId,
    });

    setSessionToDuplicateId(undefined);
    setIsLoading(false);
    setOpenModal(false);
  }, [duplicateCriteriaField, fields, form, formToDuplicate, onChange]);

  const onDuplicate = useFeatureRequiredCallback(
    Feature.SCREENING_DUPLICATION,
    () => {
      setOpenModal(true);
    },
    session?.planId,
  );

  return (
    <div className={classes.mainContainer}>
      <IconMutedButton
        icon="ri-file-copy-line"
        iconColor="ink"
        text={t('screeningForm.create.questionBuilder.duplicateSessionForm')}
        onClick={onDuplicate}
        className={classes.buttonModal}
        lockOnFeature={Feature.SCREENING_DUPLICATION}
        planId={session?.planId}
      />
      <CenteredModal
        buttons={[
          <Button
            key="buttonDuplicate"
            color="ink"
            variant="muted"
            onClick={onDuplicateSessionFormSubmit}
            small
            className={classes.buttonDuplicate}
            isLoading={loading}
            disabled={
              numberOfQuestionsToDuplicate === 0 ||
              (maxNumberOfQuestions < numberOfQuestionsToDuplicate &&
                maxNumberOfQuestions !== -1)
            }
          >
            {t('screeningForm.duplicate.button')}
            <i className={classnames('ri-file-copy-line', classes.icon)} />
          </Button>,
        ]}
        title={t('screeningForm.duplicate.title')}
        isOpen={openModal}
        onRequestClose={() => setOpenModal(false)}
      >
        <div className={classes.duplicateContainer}>
          <LabeledSection label={t('screeningForm.duplicate.select')}>
            <Select
              containerClass={classes.select}
              options={allSessionId}
              onChange={(e: { value: string }) => {
                setSessionToDuplicateId(e.value);
              }}
              value={sessionToDuplicateId}
            />
          </LabeledSection>
          {sessionToDuplicateId && (
            <>
              {numberOfQuestionsToDuplicate === 0 && (
                <InformationBox
                  text={t('screeningForm.duplicate.noQuestionToDuplicate')}
                />
              )}
              {maxNumberOfQuestions < numberOfQuestionsToDuplicate &&
                maxNumberOfQuestions !== -1 && (
                  <InformationBox
                    text={t(
                      'screeningForm.duplicate.toMuchQuestionToDuplicate',
                    )}
                  />
                )}
              {numberOfQuestionsToDuplicate !== 0 &&
                (maxNumberOfQuestions >= numberOfQuestionsToDuplicate ||
                  maxNumberOfQuestions === -1) &&
                numberOfQuestions !== 0 && (
                  <InformationBox
                    variant="danger"
                    text={t('screeningForm.duplicate.warnDuplicateOverwrite')}
                  />
                )}
            </>
          )}
        </div>
      </CenteredModal>
      <CenteredModal
        buttons={[
          <Button
            key="buttonOk"
            color="ink"
            variant="muted"
            onClick={() => setIsUnmoderatedDuplicationNotConform(false)}
            small
            className={classes.buttonDuplicate}
          >
            {t('screeningForm.duplicate.ok')}
          </Button>,
        ]}
        title={t('screeningForm.duplicate.title')}
        isOpen={isUnmoderatedDuplicationNotConform}
        onRequestClose={() => setIsUnmoderatedDuplicationNotConform(false)}
      >
        <InformationBox
          text={
            <Trans
              i18nKey="screeningForm.duplicate.carefulDuplicateUnmoderated"
              components={{
                check: (
                  <IconChip
                    icon="ri-check-line"
                    color="green"
                    className={classes.iconContainer}
                    iconClassName={classes.iconText}
                  />
                ),
                cross: (
                  <IconChip
                    icon="ri-close-line"
                    color="red"
                    className={classes.iconContainer}
                    iconClassName={classes.iconText}
                  />
                ),
              }}
            />
          }
          variant="info"
        />
      </CenteredModal>
    </div>
  );
};
